// BHP-1050: account view + edit against Backend2.0. Only app-owned profile
// fields (name) are editable; email + password are owned by Authentik (BHP-1093)
// so email is read-only and there is no password form (the unused local
// Account.password is removed in BHP-1092).
//
// UI ported from Claude Design ("Account bewerken"): a light slate/teal theme
// with an avatar, a 3-column name grid, card headers with a footer status/actions
// bar, dirty-tracking (Save/Discard disabled until changed) and required markers.
// Colours use Tailwind's slate/teal/rose scale (this is a page, not shared/ui).
import { type FormEvent, useState } from 'react';
import { useCurrentAccount } from '../../features/account/application/useCurrentAccount';
import { useUpdateAccount } from '../../features/account/application/accountMutations';
import type { AccountData } from '../../features/account/domain/accountData';
import { toFormErrors } from '../../shared/http/errorMessage';
import env from '../../config/env';

interface ProfileForm {
  firstName: string;
  insertion: string;
  lastName: string;
}

function initialForm(account: AccountData): ProfileForm {
  return {
    firstName: account.firstName ?? '',
    insertion: account.insertion ?? '',
    lastName: account.lastName ?? ''
  };
}

/**
 * BHP-1048: the inputs this page renders with an error slot. UpdateAccountDto
 * also carries email, roles, features and active — none of which are editable
 * here — so a violation on one of those belongs in the status line rather than
 * being bound to an input that does not exist.
 */
const ERROR_BEARING_FIELDS: readonly string[] = [
  'firstName',
  'insertion',
  'lastName'
];

function initialsOf(form: ProfileForm): string {
  const first = form.firstName.trim().charAt(0);
  const last = form.lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase() || '–';
}

function fullNameOf(form: ProfileForm): string {
  return [form.firstName, form.insertion, form.lastName]
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(' ');
}

// BHP-1093: identity (email/password) lives in Authentik. Derive the user
// self-service portal from the OIDC authority's origin so the account page can
// link out to it instead of editing the local Account.
function authentikPortalUrl(): string {
  try {
    return `${new URL(env.auth.authority).origin}/if/user/`;
  } catch {
    return env.auth.authority;
  }
}

export default function EditAccount(): JSX.Element {
  const { data: account, isPending, isError } = useCurrentAccount();

  if (isPending) {
    return <p className="text-slate-500">Loading account…</p>;
  }
  if (isError || account === null || account === undefined) {
    return <p className="text-slate-500">Your account could not be loaded.</p>;
  }

  return (
    <div className="flex max-w-[760px] flex-col gap-5">
      {/* key resets the form state when the identity changes (e.g. a different
          account), while preserving in-progress edits across same-account refetches. */}
      <ProfileCard key={account.uuid} account={account} />
      <SignInCard email={account.email} />
    </div>
  );
}

function ProfileCard({ account }: { account: AccountData }): JSX.Element {
  const profile = useUpdateAccount(account.uuid);
  const [form, setForm] = useState<ProfileForm>(() => initialForm(account));
  const [baseline, setBaseline] = useState<ProfileForm>(() =>
    initialForm(account)
  );

  const dirty =
    form.firstName !== baseline.firstName ||
    form.insertion !== baseline.insertion ||
    form.lastName !== baseline.lastName;
  const saveFailure = toFormErrors(
    profile.error,
    'Could not save your changes. Please try again.',
    ERROR_BEARING_FIELDS
  );
  const errors = saveFailure.fields;
  const missingRequired =
    form.firstName.trim() === '' || form.lastName.trim() === '';
  const canSave = dirty && !missingRequired && !profile.isPending;

  function set(field: keyof ProfileForm, value: string): void {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSave) {
      return;
    }
    const insertion = form.insertion.trim();
    profile.mutate(
      {
        firstName: form.firstName.trim(),
        insertion: insertion === '' ? null : insertion,
        lastName: form.lastName.trim()
      },
      { onSuccess: () => setBaseline({ ...form }) }
    );
  }

  const statusText = profile.isPending
    ? 'Saving your changes…'
    : dirty
      ? 'You have unsaved changes'
      : 'All changes saved';

  // BHP-1048: whatever could not be bound to an input, so the save never fails
  // silently. The old version only rendered when there were NO field errors at
  // all, which hid a violation on a field this page does not show; it also put
  // the HTTP status on screen, which tells the user nothing.
  const generalError = saveFailure.message;

  return (
    <section className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm">
      <div className="bg-slate-50 px-6 py-4">
        <h2 className="text-base font-semibold text-slate-900">Profile</h2>
        <p className="mt-1 text-[13px] text-slate-500">
          This name is shown on invoices, tickets and in the platform.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-teal-100 text-lg font-semibold text-teal-700">
              {initialsOf(form)}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-slate-800">
                {fullNameOf(form) || 'Your name'}
              </span>
              <span className="text-xs text-slate-400">
                Initials are generated from your name
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.4fr_0.8fr_1.4fr]">
            <Field
              label="First name"
              required
              value={form.firstName}
              placeholder="Jan"
              onChange={(value) => {
                set('firstName', value);
              }}
              error={errors.firstName}
            />
            <Field
              label="Insertion"
              value={form.insertion}
              placeholder="van der"
              onChange={(value) => {
                set('insertion', value);
              }}
              error={errors.insertion}
            />
            <Field
              label="Last name"
              required
              value={form.lastName}
              placeholder="Jansen"
              onChange={(value) => {
                set('lastName', value);
              }}
              error={errors.lastName}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 bg-slate-50 px-6 py-3">
          <span
            className={`text-[13px] ${generalError !== null ? 'text-rose-500' : 'text-slate-500'}`}
          >
            {generalError ?? statusText}
          </span>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => {
                setForm(baseline);
              }}
              disabled={!dirty || profile.isPending}
              className="rounded-lg border border-solid border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="rounded-lg border-0 bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {profile.isPending ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

function SignInCard({ email }: { email: string | null }): JSX.Element {
  return (
    <section className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm">
      <div className="bg-slate-50 px-6 py-4">
        <h2 className="text-base font-semibold text-slate-900">
          Sign-in details
        </h2>
        <p className="mt-1 text-[13px] text-slate-500">
          Your e-mail and password are managed in Authentik.
        </p>
      </div>
      <div className="flex flex-col gap-5 px-6 py-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-[13px] text-slate-400">Email</span>
            <span className="text-sm font-medium text-slate-800">
              {email ?? '—'}
            </span>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
            Managed by Authentik
          </span>
        </div>
        <a
          href={authentikPortalUrl()}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 self-start rounded-lg border border-solid border-teal-200 bg-white px-4 py-2.5 text-sm font-medium text-teal-700 hover:bg-teal-50"
        >
          Change in Authentik
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M14 4h6v6" />
            <path d="M20 4l-9 9" />
            <path d="M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
          </svg>
        </a>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium text-slate-600">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="w-full rounded-lg border border-solid border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
      />
      {error !== undefined && (
        <span className="text-[13px] text-rose-500">{error}</span>
      )}
    </label>
  );
}
